'use client';

import { type ReactNode, useLayoutEffect, useRef } from 'react';
import { cn } from '../../utils/cn';
import { useUiMotionEnabled } from '../../hooks/useMotion';

export interface Tab<T extends string> {
  id: T;
  label: ReactNode;
}

interface TabFilterProps<T extends string> {
  tabs: Tab<T>[];
  activeTab: T;
  onChange: (tab: T) => void;
  className?: string;
}

export const TabFilter = <T extends string>({ tabs, activeTab, onChange, className }: TabFilterProps<T>) => {
  const motionEnabled = useUiMotionEnabled();
  const scrollRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    const container = scrollRef.current;
    const active = activeRef.current;
    if (!container || !active) return;
    if (container.scrollWidth <= container.clientWidth) return;
    const left = active.offsetLeft - container.clientWidth / 2 + active.clientWidth / 2;
    container.scrollTo({ left, behavior: 'smooth' });
  }, [activeTab]);

  return (
    <div className={cn('w-full px-4 py-2', className)}>
      {/* the glass surface (bg / border / ::before tint / backdrop-blur) lives on this
          NON-scrolling wrapper; only the inner row scrolls. Keeping them on one element made
          the ::before tint (absolute inset-0 → sized to scrollWidth) slide with the scroll. */}
      <div
        className={cn(
          'relative h-[32px] p-[2px] rounded-full overflow-hidden',
          'liquid-glass backdrop-blur-[15px] backdrop-saturate-[180%] backdrop-brightness-125'
        )}
      >
        <div
          ref={scrollRef}
          className={cn(
            'relative z-[1] flex h-full items-center gap-[4px]',
            'overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch]',
            '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
          )}
        >
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              ref={isActive ? activeRef : undefined}
              type="button"
              onClick={() => onChange(tab.id)}
              className={cn(
                'relative z-[1] flex-1 min-w-max h-full flex items-center justify-center px-[10px] py-[3px] rounded-[20px]',
                'border-none cursor-pointer outline-none [-webkit-tap-highlight-color:transparent] transition-colors duration-150',
                // The press step is separate from the travelling selection plate, and it fires
                // on the active segment too — otherwise re-tapping the current tab reads as a
                // tap that was not caught.
                'active:shadow-[inset_0_0_0_9999px_rgba(255,255,255,0.08)]',
                motionEnabled && 'shot-press-tint',
                isActive ? 'bg-white/[0.18]' : 'bg-transparent hover:bg-white/[0.04]'
              )}
            >
              <span
                className={cn(
                  'whitespace-nowrap text-center text-label-primary',
                  'text-[13.333px] leading-[18px] tracking-[-0.08px]',
                  isActive ? 'font-[590]' : 'font-[510]'
                )}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
        </div>
      </div>
    </div>
  );
};
